import React from "react";
import styled from "styled-components";
import colors from "../../assets/colors";
import { mobileScreen } from "../../assets/screens";
import { FontAwesome } from "../Bootstrap";
import type { CaptureSide } from "../../types/submission";

interface CaptureEntry {
  url: string;
  view: string;
  alias: string;
}

type CaptureMap = Map<string, CaptureEntry>;

interface UploadProgressGridProps {
  captureSides: CaptureSide[];
  captures: CaptureMap | CaptureEntry[] | null | undefined;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  isLoading: boolean;
}

interface ThumbWrapperProps {
  isCurrent?: boolean;
  isUploaded?: boolean;
}

interface ItemLabelProps {
  isCurrent?: boolean;
  isUploaded?: boolean;
}

interface PlaceholderProps {
  isCurrent?: boolean;
}

const toCaptureMap = (captures: UploadProgressGridProps["captures"]): CaptureMap =>
  captures instanceof Map ? captures : new Map();

export default function UploadProgressGrid({
  captureSides,
  captures,
  currentIndex,
  setCurrentIndex,
  isLoading,
}: UploadProgressGridProps) {
  const captureMap = toCaptureMap(captures);
  const uploadedCount = captureSides.filter((side) =>
    captureMap.has(side.view)
  ).length;
  const totalCount = captureSides.length;

  return (
    <Container>
      <GridLabel>
        {uploadedCount}/{totalCount} photos
      </GridLabel>

      <Grid>
        {captureSides.map((side, index) => {
          const capture = captureMap.get(side.view);
          const isUploaded = Boolean(capture?.url);
          const isCurrent = currentIndex === index;
          const isLoadingThis = isLoading && isCurrent;

          return (
            <GridItem
              key={side.alias}
              onClick={() => setCurrentIndex(index)}
              type="button"
              aria-label={`${side.view}${isUploaded ? ', uploaded' : ''}`}
            >
              <ThumbOuter>
                <ThumbWrapper isCurrent={isCurrent} isUploaded={isUploaded}>
                  {isUploaded && capture ? (
                    <Thumb src={capture.url} alt="" />
                  ) : (
                    <Placeholder isCurrent={isCurrent} />
                  )}
                  {isLoadingThis && <LoadingOverlay />}
                </ThumbWrapper>
                {isUploaded && (
                  <CheckMark>
                    <FontAwesome
                      className="fa-solid fa-check"
                      style={{ fontSize: 9 }}
                    />
                  </CheckMark>
                )}
              </ThumbOuter>
              <ItemLabel isCurrent={isCurrent} isUploaded={isUploaded}>
                {side.view}
              </ItemLabel>
            </GridItem>
          );
        })}
      </Grid>
    </Container>
  );
}

const Container = styled.div`
  margin: 20px 0 4px;
  padding: 0 16px;
`;

const GridLabel = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: ${colors.grey};
  text-align: center;
`;

const Grid = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 8px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GridItem = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 72px;

  &:focus-visible {
    outline: 2px solid ${colors.blue3};
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

const ThumbOuter = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;

  ${mobileScreen(`
    width: 56px;
    height: 56px;
  `)}
`;

const ThumbWrapper = styled.div<ThumbWrapperProps>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid
    ${(props) =>
      props.isCurrent
        ? colors.blue3
        : props.isUploaded
        ? colors.green
        : colors.grey2};
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.div<PlaceholderProps>`
  width: 100%;
  height: 100%;
  background: ${(props) =>
    props.isCurrent ? "rgba(3, 189, 195, 0.08)" : colors.grey2};
`;

const CheckMark = styled.span`
  position: absolute;
  bottom: -2px;
  right: -2px;
  z-index: 2;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.green};
  color: ${colors.white};
  font-size: 9px;
  border: 2px solid ${colors.white};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    margin: -10px 0 0 -10px;
    border: 2px solid ${colors.grey2};
    border-top-color: ${colors.blue3};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ItemLabel = styled.span<ItemLabelProps>`
  margin-top: 5px;
  font-size: 10px;
  text-align: center;
  text-transform: capitalize;
  color: ${(props) =>
    props.isCurrent ? colors.blue3 : props.isUploaded ? colors.black : colors.grey};
  font-weight: ${(props) => (props.isCurrent ? 600 : 400)};
  line-height: 1.2;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
